import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as adminChannels from '@/services/api/adminChannels.api';

const KEY = 'admin-channels';

export function useChannelProperties() {
  return useQuery({
    queryKey: [KEY, 'properties'],
    queryFn: adminChannels.listChannelProperties,
  });
}

export function usePropertyChannel(propertyId: string | null) {
  return useQuery({
    queryKey: [KEY, 'property', propertyId],
    queryFn: () => adminChannels.getPropertyChannel(propertyId!),
    enabled: !!propertyId,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [KEY] });
}

export function useCreateChannelConnection() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (data: Parameters<typeof adminChannels.createChannelConnection>[0]) =>
      adminChannels.createChannelConnection(data),
    onSuccess: invalidate,
  });
}

export function useUpdateChannelConnection() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof adminChannels.updateChannelConnection>[1] }) =>
      adminChannels.updateChannelConnection(id, data),
    onSuccess: invalidate,
  });
}

export function useDeleteChannelConnection() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => adminChannels.deleteChannelConnection(id),
    onSuccess: invalidate,
  });
}

export function useAddChannelMapping() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ connectionId, data }: { connectionId: string; data: Parameters<typeof adminChannels.addChannelMapping>[1] }) =>
      adminChannels.addChannelMapping(connectionId, data),
    onSuccess: invalidate,
  });
}

export function useDeleteChannelMapping() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ connectionId, mappingId }: { connectionId: string; mappingId: string }) =>
      adminChannels.deleteChannelMapping(connectionId, mappingId),
    onSuccess: invalidate,
  });
}

export function useSyncChannelNow() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (connectionId: string) => adminChannels.syncChannelNow(connectionId),
    onSuccess: invalidate,
  });
}
